import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { BooleanOperation, SDFOperations } from './SDFOperations';
import { maybeYield } from './utils';

/** Undo/modification label of a bulk-union write (SR-WOR-H-26-2). */
export const UNION_OPERATION = 'Union';

/**
 * Post-union stages of the bulk-union pipeline. A stage is "off" when its
 * field is `undefined` / `0`; the pipeline never runs a stage it was not
 * asked for, and never silently skips one it was.
 */
export interface UnionPipelineParams {
    /**
     * Per-slice (2D) hole fill applied immediately after the union fold,
     * BEFORE the margin; `undefined`/`false` disables the stage. The
     * primitive is the threshold segmentation's own
     * {@link SDFOperations.applyFillCavities2D} — no bespoke fill. It runs
     * first among the post-union stages because the holes it exists for
     * are CREATED by the fold (adjacent sources enclosing a gap between
     * them), the same reason the threshold pipeline fills right after its
     * own mask is made (SR-WOR-H-27-2: threshold → 2D fill → … →
     * margin): fill the cavities the producing step made, then grow —
     * a fill after the margin would instead swallow openings the margin
     * itself narrowed shut, holes the user never asked to close.
     */
    fillHoles2d?: boolean;
    /**
     * Symmetric margin in mm applied AFTER the union. Positive expands,
     * negative contracts, 0 disables the stage. The panel accepts cm and
     * converts at the control — everything from here down is mm.
     */
    marginMm: number;
    /**
     * Minimum connected-component volume in **cm³**; `undefined` disables
     * the stage. cm³ and not mm³ on purpose: {@link
     * SDFOperations.applyRemoveSmallParts} — and `removeSmallParts` behind
     * it — take a cm³ floor next to a mm³ voxel volume and convert
     * internally. The unit lives in the name at every hop so the outlier
     * stays visible.
     */
    minVolumeCm3?: number;
    /**
     * Intersect the result with this texture (keep-inside-EXTERNAL). The
     * stage is IMPLICIT — there is no user flag; the caller passes the
     * EXTERNAL's texture when the structure set has one that holds
     * geometry, and `undefined` otherwise. Read-only here; the CALLER owns
     * the texture's lifetime.
     */
    keepInsideTexture?: ContourTexture;
    /**
     * Distance in mm by which the keep-inside clip boundary lies INSIDE
     * the EXTERNAL surface — a CTV must typically stay a few millimetres
     * inside the skin rather than touch it. `0`/`undefined` clips at the
     * surface exactly (the previous behaviour). Only meaningful together
     * with {@link keepInsideTexture}; a negative value is refused, never
     * clamped. The contraction runs on a scratch copy of the EXTERNAL —
     * the EXTERNAL's own texture is never written to.
     */
    keepInsideContractionMm?: number;
}

/**
 * Union N structures and apply the post-union stages, returning ONE new
 * texture the caller owns and must `delete()`.
 *
 * Fixed stage order (SR-WOR-H-26-2):
 *
 *   union (N-way OR) → fill holes (2D) → margin → remove small components
 *   → keep-inside-EXTERNAL
 *
 * The 2D hole fill runs FIRST among the post-union stages — see
 * {@link UnionPipelineParams.fillHoles2d} for why it precedes the margin
 * (fill what the fold enclosed, then grow; the threshold-segmentation
 * pipeline orders its own fill before its margin for the same reason).
 *
 * The component floor runs AFTER the margin here, the opposite of the
 * threshold-segmentation pipeline (SR-WOR-H-27-2,
 * {@link bodyContourTextureFromMask}), and for a reason that is specific to
 * the input rather than a divergence of principle. Threshold segmentation
 * consumes thresholded image noise: its debris (couch, cables, speckle)
 * exists from voxel one, and growing it before the floor would inflate every
 * speck past the floor. A union consumes structures a human authored, so
 * after the union there is no debris to remove — a floor applied first could
 * only delete clinical volume the user explicitly selected. The only debris
 * in THIS pipeline is created by the margin itself: a negative margin
 * fragments a structure at its thin isthmuses, and a positive one merges
 * components that a pre-margin floor might have deleted first. Both pipelines
 * therefore obey the same rule — the floor judges the post-margin
 * components — and land on opposite mechanical orders.
 *
 * Keep-inside-EXTERNAL is last in both, verbatim for the same reason: the
 * margin must never regrow past the clip. Its clip boundary is optionally
 * the EXTERNAL contracted inward by {@link
 * UnionPipelineParams.keepInsideContractionMm} — a skin margin — computed
 * on a scratch copy so the EXTERNAL itself is never touched. The floor therefore judges
 * PRE-CLIP components: the clip can still trim a kept component below the
 * floor (a component overhanging the patient outline keeps its pre-clip
 * volume for the judgement). That is the deliberate cost of "the margin
 * never regrows past the clip", shared with the threshold-segmentation
 * pipeline, and the panel's tooltip states this order rather than
 * promising a post-clip judgement.
 *
 * @param sources Source textures, at least two. Read-only; the CALLER owns them.
 * @param params Post-union stages.
 * @param signal Cancellation; an abort throws `AbortError` and leaks nothing.
 * @throws When fewer than two sources are given, or when a stage fails.
 */
export async function computeUnionTexture(
    sources: readonly ContourTexture[],
    params: UnionPipelineParams,
    signal?: AbortSignal
): Promise<ContourTexture> {
    if (sources.length < 2) {
        throw new Error(
            `A union needs at least two source structures, got ${sources.length}.`
        );
    }
    if (
        params.keepInsideContractionMm !== undefined &&
        params.keepInsideContractionMm < 0
    ) {
        // Fail fast rather than clamp: a negative contraction would EXPAND
        // the clip past the patient outline, silently delivering geometry
        // outside the EXTERNAL under a control that promises the opposite.
        throw new Error(
            'The keep-inside contraction must not be negative, got ' +
                `${params.keepInsideContractionMm} mm.`
        );
    }

    // The first OR is the only allocation of the fold: it clones source 0
    // and takes applyBoolean's in-place fast path (see
    // SDFOperations.applyBooleanToNewTexture). Every later operand is
    // OR-ed into that accumulator in place.
    let result = SDFOperations.applyBooleanToNewTexture(
        sources[0]!,
        sources[1]!,
        BooleanOperation.OR
    );

    // Release-with-acquisition: from here on every throw must free the
    // accumulator, including the AbortError from maybeYield.
    try {
        let lastYieldTick = performance.now();
        for (let i = 2; i < sources.length; i++) {
            lastYieldTick = await maybeYield(lastYieldTick, signal);
            SDFOperations.applyBoolean(
                result,
                sources[i]!,
                BooleanOperation.OR,
                result
            );
        }

        if (params.fillHoles2d === true) {
            // Crop to content first: the fill reads the SDF back and
            // reinitializes it on the GPU, so an un-cropped grid costs a
            // full-volume readback (the margin stage's own rationale).
            // Safe for the border-seeded flood fill: the crop removes only
            // all-background bands, so any background region that reached
            // the old border still reaches the cropped one.
            result.cropToContentBounds();
            const filled = await SDFOperations.applyFillCavities2D(
                result,
                signal
            );
            if (!filled) {
                // undefined means the stage could NOT run (GL guard held by
                // another operation, or no SDF to read). Skipping it would
                // deliver unfilled holes under a control that promised the
                // opposite — the same fail-not-skip contract as the
                // component floor below.
                throw new Error(
                    'Could not fill holes: the GPU is busy with another ' +
                        'operation.'
                );
            }
            result.delete();
            result = filled;
            if (signal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }
        }

        if (params.marginMm !== 0) {
            // Crop to content first for the same reason the threshold
            // segmentation does (BodyContourExtraction): applyMarginBase
            // expands the grid, reads the SDF back, shifts it on the CPU
            // and crops again — running that on an un-cropped grid costs
            // several full-volume readbacks. The crop is voxel-aligned and
            // reads the warm pixel cache.
            result.cropToContentBounds();
            const grown = await SDFOperations.applySymmetricMargin(
                result,
                params.marginMm
            );
            result.delete();
            result = grown;
            if (signal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }
        }

        if (params.minVolumeCm3 !== undefined) {
            const cleaned = await SDFOperations.applyRemoveSmallParts(
                result,
                params.minVolumeCm3,
                '3D',
                signal,
                // Strict: the never-empty default would retain a lone
                // sub-floor component and hand back a union that violates
                // the configured minimum while provenance records it as
                // applied. This caller refuses an empty result itself
                // (useBulkUnion), so the floor can be honest.
                { allowEmptyResult: true }
            );
            if (!cleaned) {
                // undefined means the stage could NOT run (GL guard held by
                // another operation, or no SDF to read). Treating that as
                // "skip the stage" would hand back a result larger than the
                // user configured and present it as valid — the SaMD hazard
                // is the wrong result, not the failed compute.
                throw new Error(
                    'Could not remove small components: the GPU is busy with ' +
                        'another operation.'
                );
            }
            // An 'unchanged' stage found no component below the floor, so
            // the union already satisfies it and carries no texture to swap.
            if (cleaned.status === 'changed') {
                result.delete();
                result = cleaned.texture;
            }
        }

        if (params.keepInsideTexture !== undefined) {
            // Intersect AFTER the margin — the margin must never regrow
            // past the clip. Content-crop first so the boolean and the
            // readback its trailing crop performs run on the content
            // extent rather than the full grid.
            result.cropToContentBounds();
            const contractionMm = params.keepInsideContractionMm ?? 0;
            // The contracted clip mask, when one is requested. It contracts
            // the CLIP BOUNDARY, not the result: the mask is the EXTERNAL
            // shrunk inward by the requested distance, derived through the
            // Margin panel's own Contract path — applySymmetricMargin with
            // a negative margin (MarginOperationContent negates the entered
            // value the same way). That call reads its source into a NEW
            // texture and never writes to it, so the EXTERNAL is never
            // mutated; the scratch copy is released in the finally on every
            // path, including a throwing AND.
            let contractedClip: ContourTexture | undefined;
            try {
                let clip = params.keepInsideTexture;
                if (contractionMm > 0) {
                    contractedClip = await SDFOperations.applySymmetricMargin(
                        params.keepInsideTexture,
                        -contractionMm
                    );
                    clip = contractedClip;
                    if (signal?.aborted) {
                        throw new DOMException(
                            'Operation aborted',
                            'AbortError'
                        );
                    }
                }
                SDFOperations.applyBoolean(
                    result,
                    clip,
                    BooleanOperation.AND,
                    result
                );
            } finally {
                contractedClip?.delete();
            }
            if (signal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }
        }

        return result;
    } catch (e) {
        result.delete();
        throw e;
    }
}
